import type {
  ChainKey,
  ChainRuntime,
  Chains,
  EvmChainKey,
  NativeChainRuntime,
  WalletChainKey,
} from '@infinex/chain-config';
import { isEip7702Supported } from '@infinex/chain-config';

import type { Custody } from './custody';
import type {
  UserChainRuntimeAddress,
  UserWalletNested,
  UserWalletId,
} from './user/datasets/wallets-v2';

export { WALLET_KINDS, type WalletKind } from '@infinex/chain-config';

/** Chain-level address belonging to a user */
export type UserChainAddress = {
  chain: WalletChainKey;
  address: string;
  kind: 'EOA' | 'SCA';
  isLegacy: boolean;
  /** userWallet.id — unique per wallet+user, not per chain or address */
  walletId: string;
  /** userChainRuntimeAddress.id — unique per address+user, not per chain */
  userChainRuntimeAddressId: string;
  isPrimary: boolean;
  label: string;
  isImported: boolean;
  custody: Custody;
};

// Name of the default user wallet
export const WALLET_LABEL_MAIN = 'Infinex wallet';
// Name of the default user turnkey wallet
export const TURNKEY_WALLET_LABEL_MAIN = 'Infinex seed phrase';

export const WALLET_SOURCES = [
  'auto_on_account_creation',
  'auto_on_feature_enable',
  'user_seed_phrase_import',
  'user_private_key_import',
  'user_external_signing_device',
  'user_multi_sig_import',
  'user_created',
] as const;
export type WalletSource = (typeof WALLET_SOURCES)[number];

export const EXTERNAL_SIGNING_DEVICE_TYPES = ['trezor', 'ledger'] as const;
export type ExternalSigningDeviceType =
  (typeof EXTERNAL_SIGNING_DEVICE_TYPES)[number];

export const EXTERNAL_SIGNING_DEVICE_TYPE_LABELS: Record<
  ExternalSigningDeviceType,
  string
> = {
  trezor: 'Trezor',
  ledger: 'Ledger',
};

export const MULTI_SIG_TYPES = [
  'gnosis_safe',
  // 'squads' — Solana multi-sig (future)
] as const;
export type MultiSigType = (typeof MULTI_SIG_TYPES)[number];

export const MULTI_SIG_TYPE_LABELS: Record<MultiSigType, string> = {
  gnosis_safe: 'Safe',
};

/**
 * Why an open proposal ended. Nullable = ended before this was recorded, or
 * not yet known. `rejected` is reserved for when a rejection consumed the nonce.
 */
export const MULTI_SIG_TRANSACTION_END_REASONS = [
  'executed_elsewhere',
  'nonce_consumed',
  'removed_from_sts',
  'rejected',
] as const;
export type MultiSigTransactionEndReason =
  (typeof MULTI_SIG_TRANSACTION_END_REASONS)[number];

/**
 * What the holder of a wallet recovery access row is allowed to export.
 *
 * - 'full': every custody secret the user owns. Staff-issued rows (admin
 *   dashboard) use this — a human vetted the requester.
 * - 'infinex_only': only the main (non-imported) Infinex wallet seed phrase.
 *   Self-service lane-1 issuance mints this: the recovery-address signature
 *   proves control of the account's own wallet, not of secrets the user
 *   imported from elsewhere (docs/plans/wallet-recovery-lane1-seed-export.md).
 *
 * The `wallet_recovery_access.scope` column default is the restricted value so
 * an insert path that doesn't consider scope fails closed; the staff paths set
 * 'full' explicitly.
 */
export const WALLET_RECOVERY_ACCESS_SCOPES = ['full', 'infinex_only'] as const;
export type WalletRecoveryAccessScope =
  (typeof WALLET_RECOVERY_ACCESS_SCOPES)[number];

export const CUSTODY_SECRETS = ['seed_phrase', 'private_key'] as const;
export type CustodySecret = (typeof CUSTODY_SECRETS)[number];

// TODO multi-wallet: https://linear.app/infinex-app/issue/ENG-4924/handle-non-standard-derivation-paths
// // ("Standard" methods were empirically determined by using popular wallets - Rabby Phantom MetaMask)
// export const PATH_INCREMENT_METHODS: Record<
//   ImportedWalletChainRuntime,
//   readonly [string, ...string[]]
// > = {
//   evm: [
//     // AFAIK only Ledger Live is non-standard
//     'standard',
//     // Apparently Ledger Live bumps account'
//     'bump_account',
//   ] as const,
//   svm: [
//     // Matches e.g. Metamask and Phantom
//     'standard',
//   ] as const,
//   sui: [
//     // Matches e.g. Slush Wallet and Phantom
//     'standard',
//   ] as const,
// };

export type PathIncrementMethod /*<T extends ImportedWalletChainRuntime>*/ =
  'standard';
// TODO multi-wallet: https://linear.app/infinex-app/issue/ENG-4924/handle-non-standard-derivation-paths
// (typeof PATH_INCREMENT_METHODS)[T][number];

/**
 * @see https://help.phantom.com/hc/en-us/articles/12988493966227-What-derivation-paths-does-Phantom-wallet-support
 * @see https://www.ledger.com/learn/what-is-the-bip44-standard
 * Describes the components of a BIP32/BIP44 hierarchical deterministic (HD) wallet derivation path
 * with format`m/purpose'/coin_type'/account'/change/address_index`
 */
export const DEFAULT_ETHEREUM_WALLET = {
  curve: 'CURVE_SECP256K1',
  pathFormat: 'PATH_FORMAT_BIP32',
  path: "m/44'/60'/0'/0/0",
  addressFormat: 'ADDRESS_FORMAT_ETHEREUM',
} as const;

/**
 * Most wallet clients only bump `addressIndex`
 * @param account - BIP44 derivation path account as in `m/purpose'/coin_type'/account'/change/address_index`
 * @param addressIndex - BIP44 derivation path address index as in `m/purpose'/coin_type'/account'/change/address_index`
 */
export function getEvmDerivationPath(account: number, addressIndex: number) {
  return `m/44'/60'/${account}'/0/${addressIndex}`;
}

export const DEFAULT_SOLANA_WALLET = {
  curve: 'CURVE_ED25519',
  pathFormat: 'PATH_FORMAT_BIP32',
  path: "m/44'/501'/0'/0'",
  addressFormat: 'ADDRESS_FORMAT_SOLANA',
} as const;

/**
 * Most wallets just bump `account'`
 * @param account - BIP44 derivation path account as in `m/purpose'/coin_type'/account'/change/address_index`
 * @see https://help.phantom.com/hc/en-us/articles/12988493966227-What-derivation-paths-does-Phantom-wallet-support
 */
export function getSolanaDerivationPath(account: number) {
  return `m/44'/501'/${account}'/0'`;
}

export const DEFAULT_SUI_WALLET = {
  curve: 'CURVE_ED25519',
  pathFormat: 'PATH_FORMAT_BIP32',
  path: "m/44'/784'/0'/0'/0'",
  addressFormat: 'ADDRESS_FORMAT_SUI',
} as const;

/**
 * @param account - BIP44 derivation path account as in `m/purpose'/coin_type'/account'/change/address_index`
 */
export function getSuiDerivationPath(account: number) {
  return `m/44'/784'/${account}'/0'/0'`;
}

/**
 * NEAR uses Ed25519 keys with SLIP-0044 coin type 397
 * The address is the hex-encoded public key (implicit account)
 *
 * Standard path is m/44'/397'/0'/0'/n' where n is the address index.
 * Ledger Live uses index 0, wallet.near.org uses index 1.
 *
 * @see https://github.com/satoshilabs/slips/blob/master/slip-0044.md
 * @see https://github.com/near/near-wallet/issues/1536
 */
export const DEFAULT_NEAR_WALLET = {
  curve: 'CURVE_ED25519',
  pathFormat: 'PATH_FORMAT_BIP32',
  path: "m/44'/397'/0'/0'/0'",
  // Turnkey doesn't have native NEAR support, but we use Solana format
  // since both use Ed25519 and base58-encoded keys
  addressFormat: 'ADDRESS_FORMAT_SOLANA',
} as const;

/**
 * NEAR increments the address_index position (unlike Solana/Sui which increment account).
 * @param account - Wallet index, placed at the address_index position in the path
 */
export function getNearDerivationPath(account: number) {
  return `m/44'/397'/0'/0'/${account}'`;
}

export function getDerivationPath(chainRuntime: ChainRuntime, account: number) {
  switch (chainRuntime) {
    case 'evm':
      return getEvmDerivationPath(0, account);
    case 'nearIntents':
      return getEvmDerivationPath(0, account);
    case 'svm':
      return getSolanaDerivationPath(account);
    case 'sui':
      return getSuiDerivationPath(account);
    case 'near':
      return getNearDerivationPath(account);
    default:
      throw new Error(`Unsupported chain runtime: ${chainRuntime}`);
  }
}

type DefaultWallet =
  | typeof DEFAULT_ETHEREUM_WALLET
  | typeof DEFAULT_SOLANA_WALLET
  | typeof DEFAULT_SUI_WALLET
  | typeof DEFAULT_NEAR_WALLET;

export const DEFAULT_WALLET_BY_NATIVE_CHAIN_RUNTIME: Record<
  NativeChainRuntime,
  DefaultWallet
> = {
  evm: DEFAULT_ETHEREUM_WALLET,
  svm: DEFAULT_SOLANA_WALLET,
  sui: DEFAULT_SUI_WALLET,
  near: DEFAULT_NEAR_WALLET,
} as const;

/**
 * Maps chain runtime addresses to chain-level addresses with backwards compatible flags (`isLegacy`).
 * - For EVM, there's some complexity due to not every chain being 7702-enabled:
 *   - Infinex-generated SCA chain runtime addresses are mapped to every active EVM chain, `isLegacy` if chain is 7702 enabled (because there will be an EOA).
 *   - Imported SCAs (e.g. Safe multi-sig) are never legacy — they are permanent, not being replaced by an EOA.
 *   - EOAs to every active evm chain, is legacy always false. We don't filter out 7702 chains if `includeEoaOnAllEvmChains` is true
 * - For non-EVM, simple, just map to an EOA on every chain for the runtime. Import status doesn't matter, and `isLegacy` always false.
 * @returns Chain-level addresses
 */
export function mapChainRuntimeAddressesToChainAddresses({
  userChainRuntimeAddresses,
  activeChains,
  includeEoaOnAllEvmChains = false,
}: {
  userChainRuntimeAddresses: (Pick<
    UserChainRuntimeAddress,
    'id' | 'chainRuntime' | 'address' | 'kind' | 'isPrimary'
  > & {
    walletId: UserWalletId;
    label: string;
    isImported: boolean;
    custody: Custody;
  })[];
  activeChains: Partial<Chains>;
  includeEoaOnAllEvmChains?: boolean;
}): UserChainAddress[] {
  const activeEvmChains = Object.entries(activeChains).filter(
    ([_, chainConfig]) => chainConfig.runtime === 'evm'
  );

  return userChainRuntimeAddresses.flatMap(
    ({
      id,
      chainRuntime,
      address,
      kind,
      isPrimary,
      walletId,
      label,
      isImported,
      custody,
    }): UserChainAddress[] => {
      if (chainRuntime === 'evm') {
        if (kind === 'SCA') {
          return activeEvmChains.map(([chainKey]) => ({
            userChainRuntimeAddressId: id,
            chain: chainKey as EvmChainKey,
            address,
            kind,
            // Only seed-phrase/private-key wallet SCAs are legacy on 7702 chains (replaced by EOA).
            // Multi-sig (Safe) and external-signer SCAs are permanent — never legacy.
            isLegacy:
              custody === 'wallet' && isEip7702Supported(chainKey as ChainKey),
            isPrimary,
            walletId,
            label,
            isImported,
            custody,
          }));
        }

        // EOAs — by default only map to EIP-7702 chains;
        // when includeEoaOnAllEvmChains is true, map to all active EVM chains.
        const eoaChains = includeEoaOnAllEvmChains
          ? activeEvmChains
          : activeEvmChains.filter(([chainKey]) =>
              isEip7702Supported(chainKey as ChainKey)
            );

        return eoaChains.map(([chainKey]) => ({
          userChainRuntimeAddressId: id,
          chain: chainKey as EvmChainKey,
          address,
          kind,
          isLegacy: false,
          isPrimary,
          walletId,
          label,
          isImported,
          custody,
        }));
      } else {
        // Not EVM => map to all chains, always EOA, never legacy
        return Object.entries(activeChains)
          .filter(([_, chainConfig]) => chainConfig.runtime === chainRuntime)
          .map(([chainKey]) => ({
            userChainRuntimeAddressId: id,
            chain: chainKey as ChainKey,
            address,
            kind,
            isLegacy: false,
            isPrimary,
            walletId,
            label,
            isImported,
            custody,
          }));
      }
    }
  );
}

// Reminder:
// - useWalletsV2MainWallet() exists: you may not need to use this directly (don't re-invent the wheel)
// - users have 0 wallets during a brief window at account creation, callers must take this into account
export function findMainWallet(
  allWallets: UserWalletNested[]
): UserWalletNested | null {
  const mainWallet = allWallets
    .filter((w) => w.source === 'auto_on_account_creation')
    .find((w) => w.seedPhraseIndex === 0);

  if (allWallets.length > 0 && !mainWallet) {
    // If the user has wallets, they must have a main one.
    throw new Error('No main wallet found in wallets');
  }

  return mainWallet ?? null;
}

export function isPrivateKeyWallet(wallet?: UserWalletNested): boolean {
  return wallet?.source === 'user_private_key_import';
}
